/**
 * Typed GraphQL for scheduling an event at a place (place manager, unit 6b)
 * and changing or cancelling one coming up there (unit 6c). The places pages'
 * only writes. (The form's rules presets come from the presets page's one
 * read, `fetchPresets` in `presets-api.ts`.)
 *
 * All four are admin-only (`auth.demand('admin')`, or admin-only fields):
 * they resolve only through the staff door (a super admin's call, see
 * `request-headers.ts`), and `schedule-event.ts` offers them to no one else.
 *   - {@link CreateEventMutation}: `admin_createEvent`, which takes the place
 *     directly (`placeId`, a place's public id); the event is made at it. Also
 *     the undo of a cancel: the same event made again (a new id).
 *   - {@link CancelEventMutation}: `cancelEvent`, the undo of a create, and
 *     Cancel. An event not yet started is marked finished at once (its end set
 *     to now, so it reads as cancelled); within a minute of its start the
 *     backend ends it instead. One already finished answers success again.
 *   - {@link EventToChangeQuery}: one event, by id, with every field Change
 *     prefills and Cancel's undo makes again (`change-event.ts`).
 *   - {@link UpdateEventMutation}: `admin_updateEvent`, Change and its undo.
 *     Every field but `id` may be left out and is then left as it is, but one:
 *     `attachedProductIds` left out DETACHES every product (the resolver's
 *     `input.attachedProductIds ?? []`, pinned by its own cascade test), so
 *     `change-event.ts` always sends the event's own back.
 *
 * `place-events-api.test.ts` keeps the read under the backend's query cost limit.
 */

import { graphql, gqlRequestAs, type RequestOptions, type ResultOf, type VariablesOf } from '@/lib/graphql';
import type { RequestViewer } from '@/lib/request-headers';

export const CreateEventMutation = graphql(`
  mutation ScheduleEvent($input: AdminCreateEventInput!) {
    admin_createEvent(input: $input) {
      event {
        id
        title
        startAt
      }
    }
  }
`);

/** What `admin_createEvent` takes, exactly as the schema types it. */
export type CreateEventInput = VariablesOf<typeof CreateEventMutation>['input'];

/** The event as made: its public id, title and start. */
export type MadeEventData = ResultOf<typeof CreateEventMutation>['admin_createEvent']['event'];

/** Make one event. Only ever called for a super admin. Every field read back
 *  is non-null, so any error fails the whole answer (`lost-answer.ts` tells a
 *  refusal from a failure after the save, e.g. an error below the root). */
export async function createEvent(input: CreateEventInput, viewer: RequestViewer): Promise<MadeEventData> {
  const data = await gqlRequestAs(viewer, CreateEventMutation, { input });
  return data.admin_createEvent.event;
}

export const CancelEventMutation = graphql(`
  mutation UndoScheduledEvent($eventId: String!) {
    cancelEvent(eventId: $eventId) {
      success
    }
  }
`);

/** Cancel one event, by public id. Only ever called for a super admin. */
export async function cancelEvent(eventId: string, viewer: RequestViewer): Promise<void> {
  await gqlRequestAs(viewer, CancelEventMutation, { eventId });
}

/** How many of an event's guests the read asks for: the backend's page cap
 *  (`Pagination.fromArgs(args, 100)`). A longer list cannot be made again
 *  from here (`change-event.ts` says so). Passed as a variable, so the cost
 *  limit does not multiply the list by it. */
export const GUESTS_READ = 100;

/** One event as Change prefills it and Cancel's undo makes it again: its
 *  times, game and presets (by slug and name; the lobby's through the
 *  admin-only `eventConfig`, as the event has no field of its own for it),
 *  season, own place, host, and every other field `admin_createEvent` takes
 *  that the event reads back. Not read back anywhere: a scoring rule's name
 *  and description, so its scoring rules are counted only. */
export const EventToChangeQuery = graphql(`
  query EventToChange($id: String!, $guests: Int!) {
    event(id: $id) {
      id
      title
      description
      stateUIHint
      startAt
      revealAt
      beginWindDownAt
      endAt
      visibility
      featured
      gatheringCount
      showFinalResultsDuration
      hasCountdownState
      countdownTime
      matchmakingEnabled
      matchmakingWaitingText
      subgatheringViewMode
      deepLinkToSubgatheringsEnabled
      howItWorksIntercomArticleId
      tileBackgroundCloudPath
      tileBackground {
        skinnyImageUrl
        wideImageUrl
      }
      overrideMatchmakingPlayerCounts
      overrideMatchmakingMinPlayerCount
      overrideMatchmakingMaxPlayerCount
      overrideMatchmakingWaitTimeForPartialMatchMs
      overrideMatchmakingAbsoluteMaxWaitTimeMs
      gatheringActivity {
        slug
        name
      }
      gatheringSettings {
        slug
        name
      }
      eventConfig {
        lobbyGatheringSettingsTag {
          slug
          name
        }
      }
      host {
        public {
          username
        }
      }
      gatheringGameMode {
        id
      }
      flatPrizingConfiguration {
        id
      }
      season {
        id
        title
      }
      ownPlace {
        id
      }
      eventTemplate {
        id
      }
      attachedProducts {
        product {
          id
        }
      }
      guestList(first: $guests) {
        totalCount
        nodes {
          public {
            username
          }
        }
      }
      leaderboard {
        title
        scoringMetrics {
          id
        }
      }
      followerCount
    }
  }
`);

/** One event as {@link EventToChangeQuery} reads it. */
export type EventToChangeData = NonNullable<ResultOf<typeof EventToChangeQuery>['event']>;

/** Read one event to change or cancel; null when it is gone. Only ever
 *  called for a super admin. */
export async function fetchEventToChange(
  eventId: string,
  viewer: RequestViewer,
  options?: RequestOptions,
): Promise<EventToChangeData | null> {
  const data = await gqlRequestAs(viewer, EventToChangeQuery, { id: eventId, guests: GUESTS_READ }, options);
  return data.event;
}

export const UpdateEventMutation = graphql(`
  mutation ChangeScheduledEvent($input: AdminUpdateEventInput!) {
    admin_updateEvent(input: $input) {
      event {
        id
        title
        startAt
      }
    }
  }
`);

/** What `admin_updateEvent` takes, exactly as the schema types it. */
export type UpdateEventInput = VariablesOf<typeof UpdateEventMutation>['input'];

/** Change one event: only the fields in `input` (and `id`). Only ever called
 *  for a super admin. Every field read back is non-null, so any error fails
 *  the whole answer (`lost-answer.ts` tells a refusal from a failure after
 *  the save). */
export async function updateEvent(input: UpdateEventInput, viewer: RequestViewer): Promise<void> {
  await gqlRequestAs(viewer, UpdateEventMutation, { input });
}
